import { useCallback, useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { UserPlus, TrendingUp, CalendarDays, CalendarRange, Sun } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const POLL_MS = 30000;

const Metric = ({ icon: Icon, label, value, sub, testid }) => (
  <div
    className="flex items-start gap-3 rounded-lg border border-brand-line bg-white p-4"
    data-testid={testid}
  >
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
      <Icon className="h-5 w-5" strokeWidth={1.8} />
    </div>
    <div className="min-w-0">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-0.5 text-2xl font-semibold leading-tight text-brand-ink" data-testid={`${testid}-value`}>
        {value ?? "—"}
      </p>
      {sub ? <p className="text-xs text-slate-400">{sub}</p> : null}
    </div>
  </div>
);

const dayLabel = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric" });
const weekLabel = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" });

export const WhatsAppInsights = () => {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/whatsapp/insights");
      setData(data);
    } catch (e) {
      /* silent on poll / forbidden */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  if (!data) return null;

  const daily = (data.daily || []).map((d) => ({ ...d, label: dayLabel(d.date) }));
  const weekly = (data.weekly || []).map((w) => ({ ...w, label: weekLabel(w.week_start) }));

  return (
    <Card className="mb-4 border-brand-line shadow-none" data-testid="wa-insights">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <TrendingUp className="h-4 w-4 text-emerald-600" strokeWidth={1.8} /> WhatsApp Insights
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Metric
            icon={UserPlus}
            label="New Enquiries — All Time"
            value={data.new_all_time}
            sub="First-time contacts"
            testid="wa-insights-new-alltime"
          />
          <Metric
            icon={CalendarRange}
            label="New Enquiries — This Month"
            value={data.new_month}
            sub="First-time contacts"
            testid="wa-insights-new-month"
          />
          <Metric
            icon={CalendarDays}
            label="New Enquiries — This Week"
            value={data.new_week}
            sub="First-time contacts"
            testid="wa-insights-new-week"
          />
          <Metric
            icon={Sun}
            label="New Enquiries — Today"
            value={data.new_today}
            sub="First-time contacts"
            testid="wa-insights-new-today"
          />
        </div>
        <div className="grid gap-5 lg:grid-cols-2">
          <div data-testid="wa-insights-daily-chart">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Daily New Enquiries — last 7 days
            </p>
            <div style={{ height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={daily} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                  <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis fontSize={11} allowDecimals={false} tickLine={false} axisLine={false} />
                  <ReTooltip cursor={{ fill: "rgba(16,185,129,0.08)" }} />
                  <Bar dataKey="count" name="New enquiries" fill="#059669" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div data-testid="wa-insights-weekly-chart">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Weekly New Enquiries — last 6 weeks
            </p>
            <div style={{ height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weekly} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                  <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis fontSize={11} allowDecimals={false} tickLine={false} axisLine={false} />
                  <ReTooltip cursor={{ fill: "rgba(16,185,129,0.08)" }} />
                  <Bar dataKey="count" name="New enquiries" fill="#0f766e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default WhatsAppInsights;
